import { useEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";

import { HR, Input, Label, MuiButton, Select } from "@ryde/controls";
import { LayoutRightCrudContentStatic } from "@ryde/layout";
import { useProfileActions } from "@ryde/redux";
import {
  appLanguageSelector,
  dateFormatSelectOptions,
  formatPhoneLocal,
  languageSelectOptions,
  profileDtoAtom,
  timeFormatSelectOptions,
  type ICreatableSelectOptions,
} from "@ryde/shared";

import { ConnectedAppsSection } from "../connected-apps";
import { ProfileFormContent } from "./profile.page.styled";

export const ProfilePageMain: FC = () => {
  const profile = useRecoilValue(profileDtoAtom);

  const { updateProfile } = useProfileActions();
  const [language, setLanguage] = useRecoilState(appLanguageSelector);

  const [dateFormat, setDateFormat] = useState<string | undefined>();
  const [timeFormat, setTimeFormat] = useState<string | undefined>();

  const { t } = useTranslation();
  useEffect(() => {
    if (profile?.metadata) {
      setDateFormat(profile.metadata.dateFormat);
      setTimeFormat(profile.metadata.timeFormat);
    }
  }, [
    profile?.metadata,
    profile?.metadata?.dateFormat,
    profile?.metadata?.timeFormat,
  ]);

  const onSaveClick = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    e.preventDefault();

    updateProfile({
      metadata: {
        language,
        dateFormat,
        timeFormat,
      },
    });
  };

  return (
    <LayoutRightCrudContentStatic>
      <ProfileFormContent>
        {!!profile && (
          <>
            <h2>
              {t("profile.hello")}, {profile?.firstName}!
            </h2>
            <HR></HR>

            <h3>{t("profile.personalDetails")}</h3>

            <Label required>{t("profile.firstName")}</Label>
            <Input
              placeholder={t("profile.firstName")}
              defaultValue={profile.firstName}
              disabled
            />

            <Label required>{t("profile.lastName")}</Label>
            <Input
              placeholder={t("profile.lastName")}
              defaultValue={profile.lastName}
              disabled
            />

            <Label required>{t("profile.email")}</Label>
            <Input
              placeholder={t("profile.email")}
              defaultValue={profile.email}
              disabled
            />

            <Label required>{t("profile.phone")}</Label>
            <Input
              placeholder={t("profile.phone")}
              defaultValue={formatPhoneLocal(profile.phone)}
              disabled
            />

            <HR />
            <h3>{t("profile.personalPreferences")}</h3>

            <Label>{t("profile.language")}</Label>
            <Select
              options={languageSelectOptions}
              isClearable={false}
              defaultValue={languageSelectOptions.find(
                el => el.value === profile?.metadata?.language,
              )}
              onChange={e => {
                const nextLanguage = (e as ICreatableSelectOptions | null)
                  ?.value;

                if (nextLanguage === "en" || nextLanguage === "he") {
                  setLanguage(nextLanguage);
                }
              }}
            />

            <Label>{t("profile.dateFormat")}</Label>
            <Select
              options={dateFormatSelectOptions}
              isClearable={true}
              defaultValue={dateFormatSelectOptions.find(
                el => el.value === profile?.metadata?.dateFormat,
              )}
              onChange={e =>
                setDateFormat((e as ICreatableSelectOptions)?.value as string)
              }
            />

            <Label>{t("profile.timeFormat")}</Label>
            <Select
              options={timeFormatSelectOptions}
              isClearable={true}
              defaultValue={timeFormatSelectOptions.find(
                el => el.value === profile?.metadata?.timeFormat,
              )}
              onChange={e =>
                setTimeFormat((e as ICreatableSelectOptions)?.value as string)
              }
            />

            <MuiButton onClick={onSaveClick} className="update-btn">
              {t("profile.save")}
            </MuiButton>

            <ConnectedAppsSection />
          </>
        )}
      </ProfileFormContent>
    </LayoutRightCrudContentStatic>
  );
};
